// =============================================================================
// domain/excursionStats.ts — the aggregates behind the Excursion analytics.
// -----------------------------------------------------------------------------
// PORTED FROM `tradeMfeR`, `tradeMfeCapturePct`, `dashMfeCapture` and the
// excursion cards' shared statistics — semantics, not code (D3).
//
// ── 🚨 EXCURSION AGGREGATES MUST BE SCOPED TO ONE INSTRUMENT ──
//
// The old client says it outright, and it is the rule that makes or breaks this
// whole surface:
//
//   "Layer 1 instrument filter: aggregating MAE/MFE across instruments is
//    statistically meaningless (1.5 pts on CL ≠ 1.5 pts on ES)."
//
// MAE and MFE are stored in POINTS. A median MFE of 12 points mixes a $600 ES
// move with a $24 MNQ move as though they were the same event. So every card is
// scoped to a single instrument, and the default is the MOST-TRADED one in the
// period — not "all". `all` exists only as a legacy aggregate.
//
// ⚠ This module therefore does NOT aggregate across instruments for the caller.
// It takes whatever set it is handed; the SCOPING is the caller's obligation and
// the UI makes it explicit. A helper that silently mixed instruments would be
// the easy thing to write and would produce confident, meaningless numbers.
//
// ── THE ≥1R GATE ──
//
// MFE capture is `realized / MFE`. On a trade whose MFE was a fraction of a unit
// of risk, that ratio explodes: a $5 win on a $1 favourable excursion reads as
// 500% capture. The old client gates the metric on `MFE >= 1R` for exactly that
// reason, and the gate is not optional — without it the median is noise.
// =============================================================================

/** What an excursion statistic needs from a trade. */
export interface ExcursionTrade {
  contract: string | null;
  maePoints: number | null;
  mfePoints: number | null;
  points: number | null;
  riskPoints: number | null;
  netPnl: number | null;
}

/**
 * Favourable excursion in units of planned risk.
 *
 * ⚠ `!rp` rejects 0 as well as null — a stop-less trade has no R, and dividing by
 * zero would yield Infinity and poison every median it reached.
 */
export function mfeR(t: ExcursionTrade): number | null {
  const mfe = t.mfePoints;
  const rp = t.riskPoints;
  if (mfe == null || !Number.isFinite(mfe) || !rp) return null;
  return mfe / rp;
}

/** Adverse excursion in units of planned risk. `> 1` means the stop was blown. */
export function maeR(t: ExcursionTrade): number | null {
  const mae = t.maePoints;
  const rp = t.riskPoints;
  if (mae == null || !Number.isFinite(mae) || !rp) return null;
  return mae / rp;
}

/**
 * Percentage of the favourable excursion actually banked: `realized / MFE`.
 *
 * ⚠ Closing at the best print is 100%; past it is >100% and allowed; a trade that
 * ran in your favour and closed red is NEGATIVE, and that is also allowed and
 * meaningful. Clamping any of those would hide the exact trades worth reviewing.
 *
 * ⚠ `mfe === 0` returns null — no favourable excursion means there was nothing to
 * capture, which is not the same as capturing none of it.
 */
export function mfeCapturePct(t: ExcursionTrade): number | null {
  const mfe = t.mfePoints;
  const pts = t.points;
  if (mfe == null || !Number.isFinite(mfe) || mfe === 0) return null;
  if (pts == null || !Number.isFinite(pts)) return null;
  return (pts / mfe) * 100;
}

/** The median. `null` on an empty set — never 0, which is a real median. */
export function median(values: readonly number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? (s[mid] as number) : (((s[mid - 1] as number) + (s[mid] as number)) / 2);
}

/**
 * The p-th percentile, linear interpolation. `p` in 0..1.
 *
 * ⚠ Used for "winner MAE p75" — where 75% of winners actually hurt, which is the
 * number a stop only needs to clear. A mean would be dragged by one outlier into
 * recommending a stop nobody needs.
 */
export function percentile(values: readonly number[], p: number): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  if (s.length === 1) return s[0] as number;
  const idx = (s.length - 1) * Math.min(1, Math.max(0, p));
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const w = idx - lo;
  return (s[lo] as number) * (1 - w) + (s[hi] as number) * w;
}

export interface CaptureStats {
  /** Trades that passed the ≥1R gate and had a computable capture. */
  sample: number;
  /** Median capture %, over the gated sample. */
  medianCapturePct: number | null;
  /** How many were dropped by the gate, so the sample is never unstated. */
  gatedOut: number;
}

/**
 * MFE capture over a set already scoped to ONE instrument.
 *
 * ⚠ Reports `gatedOut`. A median over 6 of 200 trades is a different claim from
 * a median over 190, and a card that shows the number without the sample is
 * quoting an average over an unstated population.
 */
export function captureStats(trades: readonly ExcursionTrade[]): CaptureStats {
  const kept: number[] = [];
  let gatedOut = 0;
  for (const t of trades) {
    const r = mfeR(t);
    const cap = mfeCapturePct(t);
    // The ≥1R gate, and it is load-bearing — see the header.
    if (r == null || r < 1 || cap == null) { gatedOut++; continue; }
    kept.push(cap);
  }
  return { sample: kept.length, medianCapturePct: median(kept), gatedOut };
}

export interface StopCalibration {
  /** Winners only: where 75% of them actually went against you, in points. */
  winnerMaeP75: number | null;
  /** Winners only: how far they typically reached. */
  winnerMfeP50: number | null;
  /** Winners only: what they actually banked. */
  winnerPointsP50: number | null;
  /** Losers only: median adverse excursion in R. `> 1` means stops were not honoured. */
  loserMaeRP50: number | null;
  winners: number;
  losers: number;
}

/**
 * Stop and target calibration, over a set scoped to ONE instrument.
 *
 * ⚠ Winners and losers are split on the $ SIGN of net P&L, which is
 * unit-invariant — the same rule `computeKpis` uses, so the two cannot disagree
 * about which trades were wins.
 *
 * The gap between `winnerMfeP50` and `winnerPointsP50` is target left on the
 * table; `winnerMaeP75` is what a stop actually has to clear. Those two numbers
 * are the whole point of the card.
 */
export function stopCalibration(trades: readonly ExcursionTrade[]): StopCalibration {
  const wMae: number[] = [];
  const wMfe: number[] = [];
  const wPts: number[] = [];
  const lMaeR: number[] = [];
  let winners = 0;
  let losers = 0;

  for (const t of trades) {
    const p = t.netPnl ?? 0;
    if (p > 0) {
      winners++;
      if (t.maePoints != null && Number.isFinite(t.maePoints)) wMae.push(Math.abs(t.maePoints));
      if (t.mfePoints != null && Number.isFinite(t.mfePoints)) wMfe.push(Math.abs(t.mfePoints));
      if (t.points != null && Number.isFinite(t.points)) wPts.push(t.points);
    } else if (p < 0) {
      losers++;
      const r = maeR(t);
      if (r != null) lMaeR.push(r);
    }
    // ⚠ Scratches are counted in NEITHER. A zero-P&L trade is not a winner whose
    // stop needs clearing, and folding it into the losers would drag the
    // stop-honoured figure toward compliance.
  }

  return {
    winnerMaeP75: percentile(wMae, 0.75),
    winnerMfeP50: median(wMfe),
    winnerPointsP50: median(wPts),
    loserMaeRP50: median(lMaeR),
    winners,
    losers,
  };
}

export interface InstrumentCount {
  symbol: string;
  count: number;
}

/**
 * Instruments present, most-traded first.
 *
 * ⚠ Keyed on the FULL contract as the old client does (`t.core.contract`), not on
 * the root. Two contract months of the same product have the same $/point, so
 * rooting them would be defensible — but it would change the default selection
 * and the counts shown beside it, and this is a parity port.
 */
export function instrumentsByVolume(trades: readonly ExcursionTrade[]): InstrumentCount[] {
  const counts = new Map<string, number>();
  for (const t of trades) {
    const c = t.contract;
    if (!c) continue;
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([symbol, count]) => ({ symbol, count }));
}

/**
 * The default instrument: the most-traded one, or `all` when there are none.
 *
 * ⚠ NOT `all` by default. Opening on the aggregate would show every user a
 * meaningless median first — mixing points across instruments — and the number
 * would look perfectly reasonable.
 */
export function defaultInstrument(trades: readonly ExcursionTrade[]): string {
  return instrumentsByVolume(trades)[0]?.symbol ?? 'all';
}
